/**
 * Minimal C++ runner using `@webcalng/clangjs`.
 * Only `runAndCollect` is used; no manual initialization or stream capture.
 */

import { runAndCollect, init } from '@webcalng/clangjs'

interface ExecutionResult {
  success: boolean;
  output: string;
  error?: string;
  executionTime: number;
  compilationError?: string;
  runtimeError?: string;
  errorType?: 'compilation' | 'runtime' | 'execution';
}

class CppCompiler {
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    try {
      await init({
        path: 'https://cdn.jsdelivr.net/npm/clang.js/dist'
      });
      this.isInitialized = true;
      console.log('[cpp] compiler initialized');
    } catch (error) {
      console.error('[cpp:error] compiler initialization failed:', error);
      throw error;
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Check if the code already has a main function
   */
  private hasMainFunction(code: string): boolean {
    // Match various forms of main: int main(), void main(), main(), etc.
    const mainRegex = /\b(int|void)?\s*main\s*\(\s*(void|int\s+\w+\s*,\s*char\s*\*\s*\*?\s*\w+|\s*)\s*\)/;
    return mainRegex.test(code);
  }

  /**
   * Wrap user code in a basic C++ template with main() if needed
   */
  private wrapCodeIfNeeded(code: string): string {
    if (this.hasMainFunction(code)) {
      // Code already has main, check if it has includes
      if (!code.includes('#include')) {
        return `#include <iostream>
#include <vector>
#include <string>
#include <algorithm>
using namespace std;

${code}`;
      }
      return code;
    }

    // Wrap the code in a template with main
    return `#include <iostream>
#include <vector>
#include <string>
#include <algorithm>
using namespace std;

${code}

int main() {
    return 0;
}`;
  }

  async executeCode(code: string, wrapCode: boolean = true): Promise<ExecutionResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    const startTime = performance.now();
    try {
      const codeToRun = wrapCode ? this.wrapCodeIfNeeded(code) : code;
      const { output, success, error } = await runAndCollect(codeToRun);
      
      return {
        success: Boolean(success) && !error,
        output: String(output ?? ''),
        error: error != null ? String(error) : undefined,
        errorType: error ? 'execution' : undefined,
        executionTime: performance.now() - startTime,
      };
    } catch (err) {
      console.log('----err----', JSON.stringify(err));
      
      const executionTime = performance.now() - startTime;
      const errorMessage = typeof err === 'object' ? JSON.stringify(err) : String(err);
      return { success: false, output: '', error: errorMessage, errorType: 'execution', executionTime };
    }
  }

  async creatFullCode(userCode: string, fullCode: string) {
    const testCase = fullCode;
    let codeToExecute = testCase.replace(
      '// USER_FUNCTION_WILL_BE_INJECTED_HERE',
      userCode
    );

    // If the first replacement didn't change anything, try the comment block format
    if (codeToExecute === testCase) {
      codeToExecute = testCase.replace(
        '/* USER_FUNCTION_WILL_BE_INJECTED_HERE */',
        userCode
      );
    }
    return codeToExecute;
  }

  async cleanOutput(output: string) {
    const stripAnsi = (text: string) => text.replace(/\u001b\[[0-9;]*m/g, '');
    const cleanedOutput = stripAnsi(output || '')
      .split(/\r?\n/)
      .map(s => stripAnsi(s).trim())
      .filter(Boolean)
      .filter(s => !/^>\s*\d+\.wasm/.test(s));

    return cleanedOutput;
  }

  async executeCodeWithTestCases(
    userCode: string,
    testCases: { input: string; expectedOutput: string; fullExecutableCode: string }[]
  ): Promise<ExecutionResult[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    console.log('----testCases----', testCases);
    const results: ExecutionResult[] = [];

    for (const testCase of testCases) {
      try {
        const hasFullCode = testCase.fullExecutableCode && testCase.fullExecutableCode.trim().length > 0;
        let codeToExecute: string;
        
        if (hasFullCode) {
          // Use the full executable code template
          codeToExecute = await this.creatFullCode(userCode, testCase.fullExecutableCode);
        } else {
          // No template provided, use user code directly (will be wrapped if needed)
          codeToExecute = userCode;
        }
        
        console.log('----fullProgram----', codeToExecute);
        const execResult = await this.executeCode(codeToExecute, !hasFullCode);
        const cleanedOutput = await this.cleanOutput(execResult.output);
        execResult.output = cleanedOutput?.join('\n') || '';
        results.push(execResult);
      } catch (err) {
        const errorMessage = typeof err === 'object' ? JSON.stringify(err) : 'Execution failed for test case';
        results.push({
          success: false,
          output: '',
          error: errorMessage,
          errorType: 'execution',
          executionTime: 0,
        });
      }
    }
    return results;
  }
}

// Export singleton
export const cppCompiler = new CppCompiler();
export default cppCompiler;